// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
//
// Declining asks for a reason, and will not commit without one. A decline the
// requester cannot understand is the dead end this whole request type exists to
// close: they can raise the ask again, so the useful thing to leave behind is why
// the answer was no. Approving needs no such step — it opens the Register dialog,
// which is its own confirmation.
import * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useIsMobile } from '@/lib/useIsMobile';
import { CONNECTION_REQUEST_CAPS } from '@greenlight/shared';
import type { ConnectionRequestQueueItem } from '@greenlight/shared';

export interface DeclineConnectionRequestDialogProps {
  /** The request being declined, or null when closed. */
  item: ConnectionRequestQueueItem | null;
  open: boolean;
  isPending: boolean;
  onConfirm: (decisionNote: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function DeclineConnectionRequestDialog({
  item,
  open,
  isPending,
  onConfirm,
  onOpenChange,
}: DeclineConnectionRequestDialogProps): React.JSX.Element {
  const isMobile = useIsMobile(768);
  const [note, setNote] = React.useState('');

  // Reset on open and on a change of target, so a note typed for one request never
  // carries over to another.
  React.useEffect(() => {
    if (open) setNote('');
  }, [open, item]);

  const trimmed = note.trim();
  const canConfirm = trimmed !== '' && !isPending;

  if (!item) {
    // A closed drawer rather than nothing, so Radix animates out gracefully.
    return (
      <GLDrawer
        variant="confirm"
        open={false}
        onOpenChange={onOpenChange}
        title=""
        srDescription="Decline connection request"
      >
        {null}
      </GLDrawer>
    );
  }

  const requester = item.requested_by.display_name ?? item.requested_by.email;

  return (
    <GLDrawer
      variant="confirm"
      open={open}
      onOpenChange={onOpenChange}
      preventDismiss={isPending}
      mobileBreakpoint={768}
      title={`Decline ${item.display_name}`}
      description={`Requested by ${requester}`}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className={isMobile ? 'h-11' : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(trimmed)}
            disabled={!canConfirm}
            className={isMobile ? 'h-11' : undefined}
          >
            {isPending ? 'Declining…' : 'Decline'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2 py-2">
        <Label htmlFor="decline-reason">Why are you declining?</Label>
        <Textarea
          id="decline-reason"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={CONNECTION_REQUEST_CAPS.decisionNoteMaxChars}
          rows={4}
          placeholder="e.g. We're consolidating on Confluence — use that instead."
        />
        <p className="text-xs text-muted-foreground">
          Recorded with the decision. {requester} can ask again later, so say what would change the
          answer.
        </p>
      </div>
    </GLDrawer>
  );
}
